import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputBase,
  Menu,
  MenuItem,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import toast from "react-hot-toast";
import { ArrowDown2, SearchNormal as SearchIcon } from "iconsax-react";
import { fetchInboxTeamData } from "../../../redux/Slice/Inbox/inboxSettingsSlice";
import {
  MAX_BULK_SELECTION,
  addToBulkSelection,
  clearBulkSelection,
  isBulkJobRunning,
  removeFromBulkSelection,
  selectActiveBulkJob,
  selectBulkSelectableIds,
  selectBulkSelectedIdsMap,
  selectBulkSelectionCount,
  selectBulkStarting,
  startInboxBulkAction,
} from "../../../redux/Slice/Inbox/inboxBulkSelectionSlice";
import { DROPDOWN_ITEM_SX, DROPDOWN_PAPER_SX } from "../../../utils/dropdownMenuStyles";
import { DARK_BUBBLE_TOOLTIP_SX } from "../../../utils/tooltipStyles";
import BulkCheckbox from "./BulkCheckbox";

// Inbox multi-select action bar — takes the place of the "All, Newest | Unreplied" filter row
// in InboxAll.jsx while conversations are selected: select-all · "N selected" · Assign · Close · ✕.
// Starting an action only queues a background job on the server (POST /api/inbox/bulk/:action);
// the chats are then closed/assigned one at a time by the existing single-chat code.

// Same values MessageCard.jsx's "..." menu Assign submenu uses, so both pickers look identical.
const SUBMENU_ITEM_SX = { ...DROPDOWN_ITEM_SX, fontSize: "0.925rem", py: 1.05, px: 1.75, mx: 0 };
const SUBMENU_PAPER_SX = { ...DROPDOWN_PAPER_SX, borderRadius: "18px" };

// Outlined neutral button, same treatment as ChatboxNavbar.jsx's own Close button.
const BAR_BUTTON_SX = {
  borderRadius: "8px",
  backgroundColor: (theme) => theme.palette.background.default,
  borderColor: (theme) => theme.palette.divider,
  "&:hover": {
    backgroundColor: (theme) => theme.palette.action.hover,
    borderColor: (theme) => theme.palette.divider,
  },
  color: (theme) => theme.palette.text.primary,
  textTransform: "none",
  fontWeight: 500,
  minWidth: 0,
  px: 0.875,
  py: 0,
  height: 28,
  fontSize: "0.8125rem",
  "& .MuiButton-startIcon": { mr: 0.5, ml: 0 },
};

const ICON_BOX_SX = { width: 16, height: 16, display: "flex", alignItems: "center", justifyContent: "center" };

// Taller than the 22px filter row it replaces (more breathing room above/below, per Rahim) —
// the list below shifts down by the difference while selecting.
export const BULK_BAR_HEIGHT = 36;

// Below this bar width the "N selected" label shows just "N" so Assign/Close/✕ always fit.
const COMPACT_LABEL_BELOW_PX = 250;

const SubmenuRowSkeleton = () => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.75, py: 1.05, mb: 0.25 }}>
    <Skeleton variant="circular" width={26} height={26} />
    <Skeleton variant="text" width="60%" height={19} />
  </Box>
);

const pluralChats = (n) => `${n} conversation${n === 1 ? "" : "s"}`;

const InboxBulkActionBar = ({ selectedInboxTab }) => {
  const dispatch = useDispatch();
  const count = useSelector(selectBulkSelectionCount);
  const selectedIdsMap = useSelector(selectBulkSelectedIdsMap);
  const selectableIds = useSelector(selectBulkSelectableIds);
  const starting = useSelector(selectBulkStarting);
  const activeJob = useSelector(selectActiveBulkJob);
  const jobRunning = isBulkJobRunning(activeJob);

  // Permissions — the same flags the single Close button / Assign submenu rely on, plus the
  // route permission each single endpoint checks (the server re-checks all of it).
  const permissions = useSelector((s) => s.userDetails.userPermissions?.permissions || {});
  const conv = permissions.Conversation || {};
  const canClose = permissions.Contact?.Edit === true;
  const canAssignTeams = permissions.Team?.View === true && permissions.Team?.Edit === true && (conv.EditAll === true || conv.EditAssignedToTeam === true);
  const canAssignMembers = permissions.TeamMember?.View === true && permissions.TeamMember?.Edit === true && (conv.EditAll === true || conv.EditAssignedToTeamMembers === true);
  const canAssign = canAssignTeams || canAssignMembers;
  const showClose = canClose && selectedInboxTab !== "Closed";

  // Select-all covers the loaded, selectable rows of the current list, up to the cap.
  const selectedVisibleCount = selectableIds.reduce((n, id) => n + (selectedIdsMap[id] ? 1 : 0), 0);
  const selectAllTarget = Math.min(selectableIds.length, MAX_BULK_SELECTION);
  const allChecked = selectAllTarget > 0 && selectedVisibleCount >= selectAllTarget;
  const someChecked = count > 0 && !allChecked;

  const handleSelectAll = () => {
    if (allChecked) {
      dispatch(removeFromBulkSelection(selectableIds));
      return;
    }
    dispatch(addToBulkSelection(selectableIds));
    if (selectableIds.length > MAX_BULK_SELECTION) {
      toast(`Selected the first ${MAX_BULK_SELECTION} conversations (maximum per action)`, { id: "inbox-bulk-max" });
    }
  };

  // Assign picker — same data/action source as the "..." menu's Assign submenu
  // (inboxSettingsSlice teams/teamMembers, fetched lazily on first open).
  const [assignAnchor, setAssignAnchor] = React.useState(null);
  const [assignSearch, setAssignSearch] = React.useState("");
  const teams = useSelector((s) => (Array.isArray(s.inboxSettings.teams) ? s.inboxSettings.teams : []));
  const teamMembers = useSelector((s) => (Array.isArray(s.inboxSettings.teamMembers) ? s.inboxSettings.teamMembers : []));
  const teamDataLoading = useSelector((s) => s.inboxSettings.teamDataLoading);
  const teamDataFetchedRef = React.useRef(false);
  const searchLower = assignSearch.trim().toLowerCase();
  const filteredTeams = teams.filter((t) => t.teamName?.toLowerCase().includes(searchLower));
  const filteredMembers = teamMembers.filter((m) => m.name?.toLowerCase().includes(searchLower));

  const openAssign = (e) => {
    setAssignAnchor(e.currentTarget);
    if (!teamDataFetchedRef.current) {
      teamDataFetchedRef.current = true;
      dispatch(fetchInboxTeamData());
    }
  };
  const closeAssign = () => {
    setAssignAnchor(null);
    setAssignSearch("");
  };

  // Confirmation — { type: "close" } or { type: "assign", targetId, targetType, targetName }.
  const [confirm, setConfirm] = React.useState(null);
  const pickTarget = (targetId, targetType, targetName) => {
    closeAssign();
    setConfirm({ type: "assign", targetId, targetType, targetName });
  };

  const handleConfirm = async () => {
    const pending = confirm;
    const contactIds = Object.keys(selectedIdsMap);
    if (!pending || contactIds.length === 0) return;
    try {
      await dispatch(
        startInboxBulkAction({
          action: pending.type,
          contactIds,
          targetId: pending.targetId,
          targetType: pending.targetType,
          targetName: pending.targetName,
        })
      ).unwrap();
      // Progress + result from here on: InboxBulkJobTracker.jsx (progress strip, then a result toast).
      setConfirm(null);
    } catch (err) {
      setConfirm(null);
      toast.error(err?.message || "Couldn't start the bulk action. Please try again.", { id: "inbox-bulk-error" });
    }
  };

  const busy = starting || jobRunning;
  const busyTooltip = jobRunning ? "A bulk action is already running" : "";

  // The chat-list column is narrow (and resizable), so when the full row wouldn't fit the
  // label drops "selected" and shows just the count — buttons keep their text size.
  const barRef = React.useRef(null);
  const [barWidth, setBarWidth] = React.useState(0);
  React.useEffect(() => {
    const el = barRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(([entry]) => setBarWidth(entry.contentRect.width));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const compactLabel = barWidth > 0 && barWidth < COMPACT_LABEL_BELOW_PX;

  return (
    <>
      <Box
        ref={barRef}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 0.5,
          pl: "4px",
          pr: "2px",
          height: BULK_BAR_HEIGHT,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, flexShrink: 0 }}>
          <Tooltip title={allChecked ? "Deselect all" : "Select all loaded"} componentsProps={{ tooltip: { sx: DARK_BUBBLE_TOOLTIP_SX } }}>
            <span>
              <BulkCheckbox
                size={22}
                checked={allChecked}
                indeterminate={someChecked}
                disabled={selectAllTarget === 0}
                onChange={handleSelectAll}
                inputProps={{ "aria-label": allChecked ? "Deselect all conversations" : "Select all loaded conversations" }}
              />
            </span>
          </Tooltip>
          <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: "text.primary", whiteSpace: "nowrap" }}>
            {compactLabel ? count : `${count} selected`}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
          {canAssign && (
            <Tooltip title={busyTooltip} componentsProps={{ tooltip: { sx: DARK_BUBBLE_TOOLTIP_SX } }}>
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={busy || count === 0}
                  onClick={openAssign}
                  endIcon={<Box sx={{ ...ICON_BOX_SX, width: 12, height: 12 }}><ArrowDown2 size="100%" color="currentColor" /></Box>}
                  sx={{ ...BAR_BUTTON_SX, "& .MuiButton-endIcon": { ml: 0.25 } }}
                >
                  Assign
                </Button>
              </span>
            </Tooltip>
          )}
          {showClose && (
            <Tooltip title={busyTooltip} componentsProps={{ tooltip: { sx: DARK_BUBBLE_TOOLTIP_SX } }}>
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={busy || count === 0}
                  onClick={() => setConfirm({ type: "close" })}
                  sx={BAR_BUTTON_SX}
                >
                  Close
                </Button>
              </span>
            </Tooltip>
          )}
          <Tooltip title="Cancel selection" componentsProps={{ tooltip: { sx: DARK_BUBBLE_TOOLTIP_SX } }}>
            <IconButton
              size="small"
              aria-label="Cancel selection"
              onClick={() => dispatch(clearBulkSelection())}
              sx={{
                p: 0.375,
                color: (theme) => theme.palette.text.secondary,
                "&:hover": { backgroundColor: (theme) => theme.palette.action.hover, color: (theme) => theme.palette.text.primary },
                "& .MuiSvgIcon-root": { fontSize: "20px" },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Menu
        anchorEl={assignAnchor}
        open={Boolean(assignAnchor)}
        onClose={closeAssign}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { minWidth: 270, maxHeight: 380, overflowY: "auto", mt: 0.5, ...SUBMENU_PAPER_SX } } }}
      >
        <Box sx={{ p: 0.75 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              position: "sticky",
              top: 0,
              zIndex: 1,
              backgroundColor: (theme) => theme.palette.background.paper,
              mb: 1,
              px: 1.25,
              py: 0.625,
              borderRadius: "8px",
              border: (theme) => `1px solid ${theme.palette.divider}`,
            }}
          >
            <Box sx={{ width: 17, height: 17, display: "flex", alignItems: "center", justifyContent: "center", color: "text.secondary", flexShrink: 0 }}>
              <SearchIcon size="100%" color="currentColor" />
            </Box>
            <InputBase
              autoFocus
              placeholder="Search teams and members"
              value={assignSearch}
              onChange={(e) => setAssignSearch(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              sx={{ fontSize: "0.925rem", flex: 1, color: "text.primary" }}
            />
          </Box>

          {teamDataLoading ? (
            <>
              <SubmenuRowSkeleton />
              <SubmenuRowSkeleton />
              <SubmenuRowSkeleton />
            </>
          ) : (
            <>
              {canAssignTeams && (
                <>
                  <Typography sx={{ pl: 1.25, pr: 1, pt: 1, pb: 0.5, fontSize: "0.7rem", fontWeight: 600, color: "text.secondary" }}>
                    Teams
                  </Typography>
                  {filteredTeams.length === 0 ? (
                    <Typography sx={{ pl: 1.25, pb: 1, fontSize: "0.75rem", color: "text.secondary", fontStyle: "italic" }}>
                      No teams found
                    </Typography>
                  ) : (
                    filteredTeams.map((team) => (
                      <MenuItem key={team.id} dense onClick={() => pickTarget(team.id, "team", team.teamName)} sx={{ ...SUBMENU_ITEM_SX, pl: 1.25 }}>
                        <Avatar sx={{ width: 26, height: 26, fontSize: "0.8125rem", bgcolor: (theme) => theme.palette.primary.main }}>
                          {team.teamName?.[0]}
                        </Avatar>
                        {team.teamName}
                      </MenuItem>
                    ))
                  )}
                </>
              )}
              {canAssignMembers && (
                <>
                  <Typography sx={{ pl: 1.25, pr: 1, pt: 1, pb: 0.5, fontSize: "0.7rem", fontWeight: 600, color: "text.secondary" }}>
                    Team Members
                  </Typography>
                  {filteredMembers.length === 0 ? (
                    <Typography sx={{ pl: 1.25, pb: 1, fontSize: "0.75rem", color: "text.secondary", fontStyle: "italic" }}>
                      No team members found
                    </Typography>
                  ) : (
                    filteredMembers.map((member) => (
                      <MenuItem key={member.id} dense onClick={() => pickTarget(member.id, "member", member.name)} sx={{ ...SUBMENU_ITEM_SX, pl: 1.25 }}>
                        <Avatar sx={{ width: 26, height: 26, fontSize: "0.8125rem", bgcolor: (theme) => theme.palette.secondary.main }}>
                          {member.name?.[0]}
                        </Avatar>
                        {member.name}
                      </MenuItem>
                    ))
                  )}
                </>
              )}
            </>
          )}
        </Box>
      </Menu>

      {/* Same look as MessageCard.jsx's delete/block confirm dialog (PaperProps, not slotProps —
          this MUI version's Dialog only reads PaperProps). */}
      <Dialog
        open={Boolean(confirm)}
        onClose={() => !starting && setConfirm(null)}
        aria-labelledby="inbox-bulk-confirm-title"
        aria-describedby="inbox-bulk-confirm-description"
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: (theme) => (theme.palette.mode === "dark" ? "0 8px 24px rgba(0,0,0,0.4)" : "0 8px 24px rgba(0,0,0,0.15)"),
            backgroundColor: (theme) => theme.palette.background.paper,
            maxWidth: 440,
          },
        }}
      >
        <DialogTitle
          id="inbox-bulk-confirm-title"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontWeight: 600,
            fontSize: { xs: "0.9375rem", sm: "1rem", md: "1.05rem", lg: "1.05rem", xl: "1.05rem" },
            padding: { xs: "12px 16px", sm: "14px 18px", md: "16px 20px", lg: "16px 20px", xl: "16px 20px" },
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <span>{confirm?.type === "assign" ? "Assign conversations" : "Close conversations"}</span>
          <IconButton
            size="small"
            aria-label="Close dialog"
            disabled={starting}
            onClick={() => setConfirm(null)}
            sx={{
              color: (theme) => theme.palette.text.secondary,
              "&:hover": { backgroundColor: (theme) => theme.palette.action.hover, color: (theme) => theme.palette.text.primary },
              "& .MuiSvgIcon-root": { fontSize: { xs: "18px", sm: "20px", md: "22px", lg: "22px", xl: "22px" } },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 2.5, md: 3 }, pb: { xs: 2, sm: 2.5 } }}>
          <Box sx={{ pt: { xs: 2.5, sm: 3, md: 3.25 } }}>
            <DialogContentText id="inbox-bulk-confirm-description" sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
              {confirm?.type === "assign"
                ? `Assign ${pluralChats(count)} to ${confirm?.targetName}?${selectedInboxTab === "Closed" ? " These closed conversations will be reopened." : ""}`
                : `Close ${pluralChats(count)}? Any automations set to run when a conversation is closed will run for each one.`}
            </DialogContentText>
            <DialogContentText sx={{ fontSize: "0.8125rem", color: "text.secondary", mt: 1 }}>
              Conversations changed by someone else in the meantime will be skipped.
            </DialogContentText>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            padding: { xs: "12px 16px 16px", sm: "14px 20px 18px", md: "16px 20px 20px" },
            justifyContent: "space-between",
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Button
            onClick={() => setConfirm(null)}
            disabled={starting}
            sx={{
              color: (theme) => theme.palette.text.secondary,
              textTransform: "none",
              fontSize: "0.8125rem",
              fontWeight: 500,
              borderRadius: "8px",
              px: 1.25,
              "&:hover": { backgroundColor: (theme) => theme.palette.action.hover },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            autoFocus
            disableElevation
            disabled={starting}
            startIcon={starting ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontSize: "0.8125rem",
              fontWeight: 600,
              px: { xs: 1.375, sm: 1.75 },
              py: { xs: 0.625, sm: 0.75 },
            }}
          >
            {confirm?.type === "assign" ? "Assign" : "Close"} {pluralChats(count)}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InboxBulkActionBar;
